import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { getVendorLogo } from "../../organization/vendorLogoUtils";
import MapTemplateConfirmDialog, {
  MapConfirmDeviceRow,
  MappingStatus,
} from "../../organization/MapTemplateConfirmDialog";
import {
  canMapTemplateToAsset,
  MapAssetTemplateDevice,
} from "./MapAssetTemplateDialog";

type TemplateOption = {
  template_id: string;
  name: string;
  vendor_name: string;
  device_model_name: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  devices: MapAssetTemplateDevice[];
  isDarkMode?: boolean;
};

const norm = (v: any) => String(v ?? "").trim().toLowerCase();

const getDeviceVendor = (device: MapAssetTemplateDevice) =>
  String(device?.vendor || device?.vendor_name || "").trim();

const getDeviceModel = (device: MapAssetTemplateDevice) =>
  String(device?.model || device?.device_model_name || device?.os || "").trim();

const getDeviceLabel = (device: MapAssetTemplateDevice) =>
  device?.device_name ||
  device?.host_name ||
  device?.name ||
  device?.host_ip_address ||
  device?.ip_address ||
  device?.id ||
  "device";

const getDeviceIp = (device: MapAssetTemplateDevice) =>
  String(device?.host_ip_address || device?.ip_address || "").trim();

const isTemplateActive = (row: any): boolean => {
  const value = row?.active ?? row?.is_active;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const n = value.trim().toLowerCase();
    return n === "true" || n === "active" || n === "1" || n === "yes";
  }
  return Boolean(value);
};

const isTemplateSuccess = (row: any): boolean => {
  const label = String(row?.status_label ?? "").trim().toLowerCase();
  if (label === "success") return true;
  return row?.status === 3 || row?.status === "3";
};

const MapMultiAssetTemplateDialog: React.FC<Props> = ({
  open,
  onClose,
  devices,
  isDarkMode = false,
}) => {
  const eligibleDevices = useMemo(
    () => (devices || []).filter((d) => canMapTemplateToAsset(d)),
    [devices]
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allTemplates, setAllTemplates] = useState<TemplateOption[]>([]);
  const [templateNamesById, setTemplateNamesById] = useState<
    Record<string, string>
  >({});
  const [mappingByDevice, setMappingByDevice] = useState<
    Record<string, { template_id: string; template_name?: string | null }>
  >({});
  /** deviceId -> selected template_id */
  const [selectedByDevice, setSelectedByDevice] = useState<
    Record<string, string>
  >({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRows, setPendingRows] = useState<MapConfirmDeviceRow[]>([]);

  const muted = isDarkMode ? "#9CA3AF" : "#667085";
  const textPrimary = isDarkMode ? "#F3F4F6" : "#101828";
  // Match Spark MasterConfig / MapAssetTemplate nested card tokens
  const paperBg = isDarkMode ? "#1A1A1A" : "#FFFFFF";
  const cardBg = isDarkMode ? "#141414" : "#FCFCFD";
  const softBg = isDarkMode ? "#1A1A1A" : "#FFFFFF";
  const cardBorder = isDarkMode ? "#2E323F" : "#E4E7EC";
  const cardShadow = isDarkMode ? "none" : "0 1px 2px rgba(16, 24, 40, 0.04)";
  const skeletonBg = isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.08)";

  const customScrollbarSx = {
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "#d1d5db",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: isDarkMode ? "rgba(255, 255, 255, 0.35)" : "#9ca3af",
    },
    scrollbarWidth: "thin" as const,
    scrollbarColor: `${isDarkMode ? "rgba(255, 255, 255, 0.2)" : "#d1d5db"} ${
      isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f1f1f1"
    }`,
  };

  const skeletonCount = Math.max(eligibleDevices.length, 1);
  const renderLoadingSkeleton = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {Array.from({ length: Math.min(skeletonCount, 3) }).map((_, idx) => (
        <Box
          key={`skeleton-${idx}`}
          sx={{
            p: 2,
            borderRadius: "12px",
            border: `1px solid ${cardBorder}`,
            bgcolor: cardBg,
            boxShadow: cardShadow,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Skeleton
              variant="rounded"
              width={72}
              height={28}
              sx={{ bgcolor: skeletonBg }}
            />
            <Skeleton
              variant="text"
              width="40%"
              height={24}
              sx={{ bgcolor: skeletonBg }}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 1.5,
              mb: 1.5,
              pb: 1.5,
              borderBottom: `1px solid ${cardBorder}`,
            }}
          >
            {[0, 1, 2].map((n) => (
              <Box key={n}>
                <Skeleton
                  variant="text"
                  width="50%"
                  height={14}
                  sx={{ bgcolor: skeletonBg }}
                />
                <Skeleton
                  variant="text"
                  width="80%"
                  height={18}
                  sx={{ bgcolor: skeletonBg }}
                />
              </Box>
            ))}
          </Box>
          <Skeleton
            variant="text"
            width={90}
            height={14}
            sx={{ bgcolor: skeletonBg, mb: 0.5 }}
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={36}
            sx={{ bgcolor: skeletonBg, borderRadius: "8px" }}
          />
        </Box>
      ))}
    </Box>
  );

  const templatesForDevice = useCallback(
    (device: MapAssetTemplateDevice) => {
      const v = norm(getDeviceVendor(device));
      const m = norm(getDeviceModel(device));
      return allTemplates.filter((t) => {
        if (v && norm(t.vendor_name) !== v) return false;
        if (m && norm(t.device_model_name) !== m) return false;
        return true;
      });
    },
    [allTemplates]
  );

  const loadData = useCallback(async () => {
    if (!open || eligibleDevices.length === 0) {
      setAllTemplates([]);
      setMappingByDevice({});
      setSelectedByDevice({});
      return;
    }

    setLoading(true);
    try {
      const [templatesRes, mappingsRes] = await Promise.all([
        axiosInstance.get(END_POINTS.MASTER_TEMPLATE_LIST, {
          params: { active: true },
        }),
        axiosInstance.get(END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING, {
          params: { active: true },
        }),
      ]);

      const rawTemplates =
        templatesRes?.data?.data?.templates ||
        templatesRes?.data?.data?.results ||
        templatesRes?.data?.data ||
        templatesRes?.data ||
        [];
      const templateList = Array.isArray(rawTemplates) ? rawTemplates : [];

      const nameById: Record<string, string> = {};
      const options: TemplateOption[] = [];
      templateList.forEach((t: any) => {
        if (!isTemplateActive(t) || !isTemplateSuccess(t)) return;
        const id = String(t.template_id || t.id || "");
        if (!id) return;
        const name = String(
          t.name || t.template_name || t.file_name || "Template"
        );
        nameById[id] = name;
        options.push({
          template_id: id,
          name,
          vendor_name: String(t.vendor_name || ""),
          device_model_name: String(t.device_model_name || ""),
        });
      });
      setTemplateNamesById(nameById);
      setAllTemplates(options);

      const rawMappings =
        mappingsRes?.data?.data?.mappings ||
        mappingsRes?.data?.data?.results ||
        mappingsRes?.data?.data ||
        mappingsRes?.data ||
        [];
      const mappings = Array.isArray(rawMappings) ? rawMappings : [];
      const idSet = new Set(eligibleDevices.map((d) => String(d.id || "")));
      const byDevice: Record<
        string,
        { template_id: string; template_name?: string | null }
      > = {};
      mappings.forEach((m: any) => {
        if (m?.active === false) return;
        const deviceId = String(m.scan_device_id || m.device_id || "");
        const tplId = String(m.template_id || m.master_template_id || "");
        if (!deviceId || !tplId || !idSet.has(deviceId)) return;
        byDevice[deviceId] = {
          template_id: tplId,
          template_name: m.template_name || m.name || nameById[tplId] || null,
        };
      });
      setMappingByDevice(byDevice);

      const defaults: Record<string, string> = {};
      eligibleDevices.forEach((d) => {
        const id = String(d.id || "");
        const v = norm(getDeviceVendor(d));
        const m = norm(getDeviceModel(d));
        const matches = options.filter((t) => {
          if (v && norm(t.vendor_name) !== v) return false;
          if (m && norm(t.device_model_name) !== m) return false;
          return true;
        });
        const current = byDevice[id]?.template_id;
        const currentStillValid = matches.some(
          (t) => t.template_id === current
        );
        // Only pre-select when already mapped; otherwise leave placeholder.
        defaults[id] = currentStillValid ? String(current) : "";
      });
      setSelectedByDevice(defaults);
    } catch (error: any) {
      console.error("Failed to load multi-asset template mapping data", error);
      toast.error(
        error?.response?.data?.message || "Failed to load config templates"
      );
      setAllTemplates([]);
      setMappingByDevice({});
      setSelectedByDevice({});
    } finally {
      setLoading(false);
    }
  }, [open, eligibleDevices]);

  useEffect(() => {
    if (open) {
      setConfirmOpen(false);
      setPendingRows([]);
      loadData();
    }
  }, [open, loadData]);

  /** Devices that have at least one matching Success template available. */
  const mappableDevices = useMemo(
    () => eligibleDevices.filter((d) => templatesForDevice(d).length > 0),
    [eligibleDevices, templatesForDevice]
  );

  /**
   * Devices ready to map: valid template selected, and either unmapped or changing
   * to a different template. Devices with no matching templates are skipped and
   * do not block the Map button.
   */
  const devicesReadyToMap = useMemo(() => {
    return mappableDevices.filter((d) => {
      const id = String(d.id || "");
      const tplId = selectedByDevice[id];
      if (!tplId) return false;
      if (!templatesForDevice(d).some((t) => t.template_id === tplId)) {
        return false;
      }
      // Already mapped to this same template — nothing to submit for this device
      if (mappingByDevice[id]?.template_id === tplId) return false;
      return true;
    });
  }, [
    mappableDevices,
    selectedByDevice,
    templatesForDevice,
    mappingByDevice,
  ]);

  const canSubmit = devicesReadyToMap.length > 0;

  const handleUnmapDevice = async (deviceId: string) => {
    if (!deviceId) return;
    setSaving(true);
    try {
      const response = await axiosInstance.post(
        END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING,
        { action: "unmap", scan_device_ids: [deviceId] }
      );
      if (response?.status === 200 || response?.data?.status_code === 200) {
        toast.success("Template unmapped from device");
        setMappingByDevice((prev) => {
          const next = { ...prev };
          delete next[deviceId];
          return next;
        });
        // Reset dropdown to placeholder — don't keep the old template selected
        setSelectedByDevice((prev) => ({
          ...prev,
          [deviceId]: "",
        }));
      } else {
        toast.error(response?.data?.message || "Failed to unmap template");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to unmap template");
    } finally {
      setSaving(false);
    }
  };

  const handleMapClick = () => {
    if (!canSubmit) {
      toast.error(
        mappableDevices.length === 0
          ? "No matching templates available for the selected devices"
          : "Select a template for at least one device"
      );
      return;
    }

    const rows: MapConfirmDeviceRow[] = [];
    devicesReadyToMap.forEach((d) => {
      const id = String(d.id || "");
      const tplId = selectedByDevice[id];
      if (!tplId) return;
      const tplName =
        allTemplates.find((t) => t.template_id === tplId)?.name ||
        templateNamesById[tplId] ||
        tplId;
      const mapped = mappingByDevice[id];
      let mapping_status: MappingStatus = "unmapped";
      if (mapped?.template_id) {
        mapping_status =
          mapped.template_id === tplId ? "mapped" : "mapped_elsewhere";
      }
      if (mapping_status === "mapped") return;
      rows.push({
        scan_device_id: id,
        hostname: getDeviceLabel(d),
        mapping_status,
        mapped_template_id: mapped?.template_id || null,
        mapped_template_name: mapped?.template_name || null,
        target_template_id: tplId,
        target_template_name: tplName,
      });
    });

    if (rows.length === 0) {
      toast.info("All selected assets are already mapped to the chosen templates");
      return;
    }
    setPendingRows(rows);
    setConfirmOpen(true);
  };

  const runMap = async () => {
    if (pendingRows.length === 0) return;
    setSaving(true);
    try {
      const byTpl = new Map<string, string[]>();
      pendingRows.forEach((r) => {
        const tplId = String(r.target_template_id || "");
        if (!tplId) return;
        const list = byTpl.get(tplId) || [];
        list.push(r.scan_device_id);
        byTpl.set(tplId, list);
      });

      let mappedCount = 0;
      for (const [template_id, scan_device_ids] of byTpl.entries()) {
        const response = await axiosInstance.post(
          END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING,
          { template_id, scan_device_ids }
        );
        if (response?.status === 200 || response?.data?.status_code === 200) {
          mappedCount += scan_device_ids.length;
        } else {
          toast.error(response?.data?.message || "Failed to map template");
          return;
        }
      }

      toast.success(
        `Mapped ${mappedCount} asset${mappedCount > 1 ? "s" : ""} to template${
          byTpl.size > 1 ? "s" : ""
        }`
      );
      setConfirmOpen(false);
      setPendingRows([]);
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to map templates");
    } finally {
      setSaving(false);
    }
  };

  const confirmTemplateName =
    Array.from(
      new Set(pendingRows.map((r) => r.target_template_name).filter(Boolean))
    ).length === 1
      ? pendingRows[0]?.target_template_name || undefined
      : undefined;

  return (
    <>
      <Dialog
        open={open && !confirmOpen}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: paperBg,
            maxHeight: "90vh",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            pt: 2.25,
            pb: 1,
            position: "relative",
            bgcolor: paperBg,
          }}
        >
          <IconButton
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: muted,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "18px",
              color: textPrimary,
              pr: 4,
            }}
          >
            Map Config Template
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: muted,
              mt: 0.35,
            }}
          >
            Choose which template applies to each device.
          </Typography>
        </Box>

        <DialogContent
          sx={{
            px: 2.5,
            pt: 1.5,
            pb: 1.5,
            maxHeight: "60vh",
            overflowY: "auto",
            bgcolor: paperBg,
            ...customScrollbarSx,
          }}
        >
          {loading ? (
            renderLoadingSkeleton()
          ) : eligibleDevices.length === 0 ? (
            <Typography sx={{ fontSize: "13px", color: "#B42318" }}>
              No eligible network devices in the selection.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {eligibleDevices.map((device) => {
                const deviceId = String(device.id || "");
                const vendorName = getDeviceVendor(device);
                const modelName = getDeviceModel(device);
                const deviceIp = getDeviceIp(device);
                const vendorLogo = getVendorLogo(vendorName, isDarkMode);
                const options = templatesForDevice(device);
                const selectedId = selectedByDevice[deviceId] || "";
                const currentMapping = mappingByDevice[deviceId] || null;

                return (
                  <Box
                    key={deviceId}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: `1px solid ${cardBorder}`,
                      bgcolor: cardBg,
                      boxShadow: cardShadow,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      {vendorLogo ? (
                        <img
                          src={vendorLogo.src}
                          alt={vendorLogo.alt || vendorName || "vendor"}
                          style={{
                            maxHeight: 36,
                            maxWidth: 90,
                            objectFit: "contain",
                            objectPosition: "left center",
                            flexShrink: 0,
                            ...(vendorLogo.style || {}),
                          }}
                        />
                      ) : null}
                      <Typography
                        sx={{
                          fontSize: "15px",
                          fontWeight: 700,
                          color: textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={getDeviceLabel(device)}
                      >
                        {getDeviceLabel(device)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 0,
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: `1px solid ${cardBorder}`,
                      }}
                    >
                      {[
                        { label: "Vendor", value: vendorName || "-" },
                        { label: "OS", value: modelName || "-" },
                        { label: "IP Address", value: deviceIp || "-" },
                      ].map((item, index) => (
                        <Box
                          key={item.label}
                          sx={{
                            minWidth: 0,
                            pr: index < 2 ? 1.25 : 0,
                            pl: index > 0 ? 1.25 : 0,
                            borderLeft:
                              index > 0
                                ? `1px solid ${cardBorder}`
                                : "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 500,
                              color: muted,
                              mb: 0.25,
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12.5px",
                              fontWeight: 700,
                              color: textPrimary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={item.value}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {options.length === 0 ? (
                      <Typography
                        sx={{ fontSize: "12.5px", color: muted, lineHeight: 1.4 }}
                      >
                        No active Success templates match this device.
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            color: muted,
                            mb: 0.5,
                          }}
                        >
                          Select template
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            displayEmpty
                            value={selectedId}
                            onChange={(e) =>
                              setSelectedByDevice((prev) => ({
                                ...prev,
                                [deviceId]: String(e.target.value),
                              }))
                            }
                            disabled={saving}
                            renderValue={(value) => {
                              if (!value) {
                                return (
                                  <Typography
                                    sx={{
                                      fontSize: "12.5px",
                                      fontWeight: 500,
                                      color: muted,
                                    }}
                                  >
                                    Select template
                                  </Typography>
                                );
                              }
                              const match = options.find(
                                (t) => t.template_id === value
                              );
                              return match?.name || String(value);
                            }}
                            sx={{
                              borderRadius: "8px",
                              bgcolor: softBg,
                              "& .MuiSelect-select": {
                                py: 0.85,
                                fontSize: "12.5px",
                                fontWeight: 600,
                                color: textPrimary,
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <Typography sx={{ fontSize: "12.5px", color: muted }}>
                                Select template
                              </Typography>
                            </MenuItem>
                            {options.map((t) => (
                              <MenuItem
                                key={t.template_id}
                                value={t.template_id}
                              >
                                {t.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {currentMapping ? (
                          <Box
                            sx={{
                              mt: 1.25,
                              px: 1.25,
                              py: 0.9,
                              borderRadius: "8px",
                              bgcolor: softBg,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: "10.5px",
                                  fontWeight: 600,
                                  color: muted,
                                }}
                              >
                                Currently mapped
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12.5px",
                                  fontWeight: 700,
                                  color: textPrimary,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={
                                  currentMapping.template_name ||
                                  currentMapping.template_id
                                }
                              >
                                {currentMapping.template_name ||
                                  templateNamesById[
                                    currentMapping.template_id
                                  ] ||
                                  currentMapping.template_id}
                              </Typography>
                            </Box>
                            <Button
                              variant="text"
                              onClick={() => handleUnmapDevice(deviceId)}
                              disabled={saving}
                              startIcon={
                                <LinkOffIcon sx={{ fontSize: 15 }} />
                              }
                              sx={{
                                flexShrink: 0,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "11.5px",
                                minWidth: 0,
                                px: 0.75,
                                color: "#B42318",
                                "&.MuiButton-text:hover": {
                                  backgroundColor: "rgba(180, 35, 24, 0.06)",
                                  color: "#B42318",
                                },
                              }}
                            >
                              Unmap
                            </Button>
                          </Box>
                        ) : null}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            pt: 1,
            pb: 2,
            gap: 1,
            bgcolor: paperBg,
            borderTop: `1px solid ${cardBorder}`,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              borderColor: isDarkMode ? "#4B5563" : "#D0D5DD",
              color: textPrimary,
              bgcolor: "transparent",
              "&.MuiButton-outlined:hover": {
                borderColor: isDarkMode ? "#6B7280" : "#98A2B3",
                backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "#F9FAFB",
                color: textPrimary,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMapClick}
            disabled={saving || loading || !canSubmit}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              bgcolor: "#3A2ADA",
              color: "#FFFFFF",
              boxShadow: "none",
              "&.Mui-disabled": {
                bgcolor: isDarkMode ? "rgba(58,42,218,0.35)" : "rgba(58,42,218,0.4)",
                color: isDarkMode ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.8)",
              },
              "&:hover": { bgcolor: "#2E21B6", boxShadow: "none", color: "#FFFFFF" },
            }}
          >
            Map Config Template ({devicesReadyToMap.length})
          </Button>
        </DialogActions>
      </Dialog>

      <MapTemplateConfirmDialog
        open={confirmOpen}
        action="map"
        rows={pendingRows}
        templateName={confirmTemplateName}
        templateNamesById={templateNamesById}
        isDarkMode={isDarkMode}
        saving={saving}
        onClose={() => {
          if (saving) return;
          setConfirmOpen(false);
          setPendingRows([]);
        }}
        onConfirm={runMap}
      />
    </>
  );
};

export default MapMultiAssetTemplateDialog;
